import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { useSEO } from "@/hooks/useSEO";

const milestones = [
  { year: "2019", title: "正式上线", text: "51网址导航首次发布，以简洁的分类索引服务第一批用户。" },
  { year: "2021", title: "分类体系升级", text: "重构站点分类与检索方式，新增全文搜索与子分类导航。" },
  { year: "2023", title: "移动端适配", text: "完成响应式改版，手机端也能流畅浏览与查找网址。" },
  { year: "2026", title: "持续进化", text: "引入收藏与智能推荐，让入口访问更贴近个人习惯。" },
];

const stats = [
  { value: "50+", label: "精选网址" },
  { value: "12", label: "大类目" },
  { value: "36", label: "子分类" },
  { value: "99.9%", label: "访问可用率" },
];

export default function AboutPage() {
  useSEO(
    "关于我们 - 51网址导航",
    "了解51网址导航的使命、愿景与发展历程，我们致力于成为权威实用的网址快速访问门户。",
    "/about",
  );

  return (
    <div>
      <section className="relative overflow-hidden bg-primary-900">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20clean%20geometric%20pattern%20with%20soft%20deep%20blue%20gradient%20and%20subtle%20diagonal%20lines%2C%20modern%20minimal%20corporate%20style%2C%20smooth%20high%20detail%2C%20no%20text&width=1920&height=520&seq=nav-about-01&orientation=landscape"
          alt="关于51网址导航"
          title="关于51网址导航"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary-900/90 via-primary-900/85 to-primary-800/90" />
        <div className="relative z-10 max-w-page mx-auto px-4 md:px-6 py-14 md:py-20 text-center">
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-background-50/15 border border-background-50/25 text-primary-100 text-xs md:text-sm mb-5">
            <i className="ri-information-line" />
            关于我们
          </span>
          <h1 className="font-heading font-extrabold text-background-50 text-3xl md:text-4xl">
            关于 51网址导航
          </h1>
          <p className="text-primary-100 text-base md:text-lg mt-3 max-w-2xl mx-auto leading-relaxed">
            我们相信，好的网络入口能让每一次访问都更高效。51网址导航专注做一件事：把值得访问的网站，规整地交到你手上。
          </p>
        </div>
      </section>

      <div className="max-w-page mx-auto px-4 md:px-6 py-6">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
      </div>

      <section className="max-w-page mx-auto px-4 md:px-6 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="p-6 md:p-8 rounded-2xl border border-background-200 bg-background-50">
            <span className="w-11 h-11 rounded-xl bg-primary-100 text-primary-600 flex items-center justify-center mb-4">
              <i className="ri-focus-3-line text-xl" />
            </span>
            <h2 className="font-heading font-bold text-lg text-foreground-950">我们的使命</h2>
            <p className="text-sm md:text-base text-foreground-600 mt-3 leading-relaxed">
              为互联网用户提供权威、实用、清晰的网址入口集合，让查找与访问热门及特色网站不再是难事，帮助每个人把时间花在真正需要的内容上。
            </p>
          </div>
          <div className="p-6 md:p-8 rounded-2xl border border-background-200 bg-background-50">
            <span className="w-11 h-11 rounded-xl bg-accent-100 text-accent-700 flex items-center justify-center mb-4">
              <i className="ri-eye-line text-xl" />
            </span>
            <h2 className="font-heading font-bold text-lg text-foreground-950">我们的愿景</h2>
            <p className="text-sm md:text-base text-foreground-600 mt-3 leading-relaxed">
              成为用户心中最可信赖的中文网址导航门户，凭借持续更新的分类索引与严选推荐，成为大家每天打开浏览器后的第一站。
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
          {stats.map((item) => (
            <div key={item.label} className="p-5 rounded-xl bg-background-100 border border-background-200 text-center">
              <p className="font-heading font-extrabold text-2xl md:text-3xl text-primary-600">{item.value}</p>
              <p className="text-xs md:text-sm text-foreground-500 mt-1.5">{item.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-background-100 border-y border-background-200">
        <div className="max-w-page mx-auto px-4 md:px-6 py-12 md:py-16">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 text-center">
            发展历程
          </h2>
          <p className="text-sm text-foreground-500 text-center mt-2 mb-10">
            一步一个脚印，陪伴用户高效上网
          </p>

          <div className="max-w-3xl mx-auto relative pl-8 md:pl-10">
            <div className="absolute left-[11px] md:left-[15px] top-2 bottom-2 w-px bg-background-300" />
            {milestones.map((item) => (
              <div key={item.year} className="relative pb-8 last:pb-0">
                <span className="absolute -left-8 md:-left-10 top-1.5 w-6 h-6 rounded-full bg-accent-500 border-4 border-background-100" />
                <div className="p-5 rounded-xl border border-background-200 bg-background-50">
                  <span className="inline-flex items-center gap-2 text-xs font-medium text-accent-700 bg-accent-100 px-2.5 py-1 rounded-full mb-2">
                    <i className="ri-flag-line" />
                    {item.year}
                  </span>
                  <h3 className="font-heading font-semibold text-base text-foreground-950">{item.title}</h3>
                  <p className="text-sm text-foreground-600 mt-2 leading-relaxed">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-page mx-auto px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">团队与联系方式</h2>
            <p className="text-sm md:text-base text-foreground-600 mt-4 leading-relaxed">
              51网址导航由一支热爱互联网产品的编辑与技术团队共同维护。我们坚持人工审核收录内容，持续跟进网站变化，确保每个入口都值得信赖。
            </p>
            <ul className="space-y-3 mt-6 text-sm">
              <li className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center">
                  <i className="ri-mail-line" />
                </span>
                <span className="text-foreground-700">客服邮箱：support@51nav.com</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center">
                  <i className="ri-customer-service-2-line" />
                </span>
                <span className="text-foreground-700">工作时间：周一至周五 09:00 - 18:00</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center">
                  <i className="ri-map-pin-line" />
                </span>
                <span className="text-foreground-700">合作与收录申请请通过联系我们页面提交</span>
              </li>
            </ul>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 mt-7 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              联系我们
              <i className="ri-arrow-right-line" />
            </Link>
          </div>

          <div className="rounded-2xl overflow-hidden border border-background-200 aspect-[4/3]">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20bright%20open%20office%20team%20collaboration%20space%20with%20people%20working%20on%20laptops%2C%20clean%20blue%20and%20white%20interior%2C%20natural%20daylight%2C%20professional%20editorial%20photography%2C%20high%20detail&width=1000&height=750&seq=nav-team-01&orientation=landscape"
              alt="51网址导航团队"
              title="51网址导航 团队与联系方式"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>
    </div>
  );
}